export const STRICT_IMAGE_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/jpg',
    // macOS 常见：HEIC/HEIF（浏览器支持情况不一）
    'image/heic',
    'image/heif',
] as const

export type StrictImageMimeType = (typeof STRICT_IMAGE_MIME_TYPES)[number]

export function isStrictSupportedImageType(file: File): boolean {
    const type = (file?.type || '').toLowerCase()
    return (STRICT_IMAGE_MIME_TYPES as readonly string[]).includes(type)
}

export async function getImageResolution(file: File): Promise<{ width: number; height: number } | null> {
    if (!file) return null
    // File/Blob 仅存在于客户端；在 SSR 环境无需支持。
    if (typeof window === 'undefined' || typeof Image === 'undefined' || typeof URL === 'undefined') return null

    // Prefer createImageBitmap when available (can be faster & avoids DOM Image in some browsers).
    try {
        const cib = (globalThis as any)?.createImageBitmap as ((blob: Blob) => Promise<ImageBitmap>) | undefined
        if (typeof cib === 'function') {
            const bmp = await cib(file)
            const width = (bmp as any)?.width ?? 0
            const height = (bmp as any)?.height ?? 0
            try {
                ;(bmp as any)?.close?.()
            } catch {
                // ignore
            }
            if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
                return { width, height }
            }
        }
    } catch {
        // fallback to <img>
    }

    const url = URL.createObjectURL(file)
    try {
        const { width, height } = await new Promise<{ width: number; height: number }>((resolve, reject) => {
            const img = new Image()
            img.onload = () => resolve({ width: img.width || 0, height: img.height || 0 })
            img.onerror = () => reject(new Error('invalid image'))
            img.src = url
        })
        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null
        return { width, height }
    } catch {
        return null
    } finally {
        try {
            URL.revokeObjectURL(url)
        } catch {
            // ignore
        }
    }
}

export function isResolutionWithinLimit(
    res: { width: number; height: number } | null,
    maxResolution: number
): boolean {
    if (!res) return false
    const maxSide = Math.max(res.width, res.height)
    return Number.isFinite(maxSide) && maxSide > 0 && maxSide <= maxResolution
}

